import apiClient from '../../shared/api/apiClient'
import { ENDPOINTS } from '../../shared/api/endpoints'
import { getRefreshToken } from '../../shared/utils/tokenStorage'

export interface LoginPayload {
  email: string
  password: string
  organizationCode?: string
  orgId?: string
  orgCode?: string
}

export interface AuthLoginResponse {
  success?: boolean
  timestamp?: string
  data?: {
    requiresPasswordReset?: boolean
    resetToken?: string
    expiresIn?: number
    accessToken?: string
    token?: string
    refreshToken?: string
    user?: any
    role?: string
    permissions?: string[]
    organizationId?: string
  }
  accessToken?: string
  token?: string
  refreshToken?: string
  expiresIn?: number
  requiresPasswordReset?: boolean
  resetToken?: string
  tokenType?: string
  user?: {
    id: string
    email: string
    name?: string
    firstName?: string
    lastName?: string
    role?: string
    permissions?: string[]
    organizationId?: string
    branchId?: string
  }
  role?: string
  permissions?: string[]
  organizationId?: string
  message?: string
}

export interface UserProfileResponse {
  id: string
  name: string
  email: string
  role: string
  permissions?: string[]
  organizationId?: string
  schoolName?: string
  avatarUrl?: string
}

export interface ResetPasswordPayload {
  resetToken: string
  newPassword: string
}

export interface ForgotPasswordPayload {
  email: string
  organizationCode?: string
}

export interface VerifyOtpPayload {
  email: string
  otp: string
  organizationCode?: string
}

export interface VerifyOtpResponse {
  resetToken?: string
  expiresIn?: number
  message?: string
}

export interface ConfirmForgotPasswordPayload {
  resetToken: string
  newPassword: string
}

export interface LogoutResponse {
  success?: boolean
  message?: string
  error?: {
    statusCode: number
    error: string
    message: string[]
    path: string
  }
  timestamp?: string
}

export const authApi = {
  /**
   * Unified Tenant/School Login — POST /api/auth/login
   */
  async login(payload: LoginPayload): Promise<AuthLoginResponse> {
    const body = {
      email: payload.email,
      password: payload.password,
      organizationCode: payload.organizationCode || payload.orgCode || payload.orgId || '',
    }
    const response = await apiClient.post<AuthLoginResponse>(ENDPOINTS.AUTH.LOGIN, body)
    return response.data
  },

  /**
   * First Login Password Reset — POST /api/auth/reset-password
   */
  async resetPasswordFirstLogin(payload: ResetPasswordPayload): Promise<void> {
    const body = {
      resetToken: payload.resetToken,
      newPassword: payload.newPassword,
    }
    try {
      await apiClient.post(ENDPOINTS.AUTH.RESET_PASSWORD, body)
    } catch {
      await apiClient.post('/auth/reset-password', body)
    }
  },

  /**
   * School Dashboard Profile Fetching: GET /api/auth/profile
   */
  async fetchUserProfile(): Promise<UserProfileResponse> {
    try {
      const response = await apiClient.get<UserProfileResponse>('/api/auth/profile')
      return response.data
    } catch {
      const response = await apiClient.get<UserProfileResponse>('/admin/profile')
      return response.data
    }
  },

  /**
   * Request (or Resend) Forgot-Password OTP — POST /api/auth/forgot-password
   */
  async requestPasswordReset(email: string, organizationCode?: string): Promise<void> {
    const payload: Record<string, any> = { email }
    if (organizationCode?.trim()) {
      payload.organizationCode = organizationCode.trim()
    }
    await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, payload)
  },

  /**
   * Verify Forgot-Password OTP — POST /api/auth/forgot-password/verify
   */
  async verifyOtp(email: string, otp: string, organizationCode?: string): Promise<VerifyOtpResponse> {
    const payload: Record<string, any> = { email, otp }
    if (organizationCode?.trim()) {
      payload.organizationCode = organizationCode.trim()
    }
    const response = await apiClient.post<VerifyOtpResponse>(ENDPOINTS.AUTH.FORGOT_PASSWORD_VERIFY, payload)
    return response.data
  },

  /**
   * Set New Password After OTP Verification — POST /api/auth/forgot-password/confirm
   */
  async confirmPasswordReset(password: string, resetToken?: string): Promise<void> {
    if (!resetToken) {
      throw new Error('Missing password reset token. Please verify OTP again.')
    }
    await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD_CONFIRM, {
      resetToken,
      newPassword: password,
    })
  },

  /**
   * Logout user session — POST /api/auth/logout
   */
  async logout(): Promise<LogoutResponse> {
    try {
      const refreshToken = getRefreshToken() || (typeof localStorage !== 'undefined' ? localStorage.getItem('refresh_token') : null) || ''
      const response = await apiClient.post<LogoutResponse>(
        ENDPOINTS.AUTH.LOGOUT,
        refreshToken ? { refreshToken, refresh_token: refreshToken } : {},
        {
          withCredentials: true,
          headers: refreshToken ? { 'x-refresh-token': refreshToken } : undefined,
        }
      )
      return response.data ?? { success: true, message: 'Logged out successfully' }
    } catch (err: any) {
      console.warn('Backend logout notification:', err?.response?.data?.message || err)
      if (err.response?.data) {
        return err.response.data
      }
      return {
        success: false,
        error: {
          statusCode: err.response?.status || 401,
          error: err.response?.statusText || 'Unauthorized',
          message: ['invalid_token'],
          path: ENDPOINTS.AUTH.LOGOUT,
        },
        timestamp: new Date().toISOString(),
      }
    }
  },

  /**
   * Authenticated User Change Password — POST /api/auth/change-password
   */
  async changePassword(payload: { currentPassword: string; newPassword: string }): Promise<void> {
    await apiClient.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, payload)
  },
}
